import {
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import {
  CREW_DAYS,
  CREW_DEFAULT_DAY_HOURS,
  CREW_TIME_SLOTS,
} from "../../../../constants";
import {
  CrewAvailability as CrewAvailabilityType,
  CrewDay,
  CrewDayHours,
} from "../../../../types";

const getTimeLabel = (time: string) =>
  CREW_TIME_SLOTS.find((slot) => slot.value === time)?.key ?? time;

const getHoursLabel = (hours: CrewDayHours) =>
  `${getTimeLabel(hours.start)} – ${getTimeLabel(hours.end)}`;

const timeSelectSx = { minWidth: 120, fontSize: 13 };

const timeMenuProps = { PaperProps: { sx: { maxHeight: 280 } } };

type CrewAvailabilityProps = {
  availability: CrewAvailabilityType;
  variant?: "compact" | "default";
  onChange?: (availability: CrewAvailabilityType) => void;
};

export const CrewAvailability = ({
  availability,
  variant = "compact",
  onChange,
}: CrewAvailabilityProps) => {
  const availableDays = CREW_DAYS.filter((day) => availability[day]).length;

  const handleToggle = (day: CrewDay) => {
    onChange?.({
      ...availability,
      [day]: availability[day] ? null : CREW_DEFAULT_DAY_HOURS,
    });
  };

  const handleStartChange = (day: CrewDay, hours: CrewDayHours) => {
    return (event: SelectChangeEvent) => {
      const start = event.target.value;
      const nextSlot = CREW_TIME_SLOTS.find(
        (slot) => String(slot.value) > start,
      );
      const end =
        hours.end > start ? hours.end : String(nextSlot?.value ?? hours.end);

      onChange?.({ ...availability, [day]: { start, end } });
    };
  };

  const handleEndChange = (day: CrewDay, hours: CrewDayHours) => {
    return (event: SelectChangeEvent) => {
      onChange?.({
        ...availability,
        [day]: { ...hours, end: event.target.value },
      });
    };
  };

  if (variant === "compact") {
    return (
      <Box>
        <Box sx={{ display: "flex", gap: "2px" }}>
          {CREW_DAYS.map((day) => {
            const hours = availability[day];

            return (
              <Box
                key={day}
                title={hours ? `${day}: ${getHoursLabel(hours)}` : day}
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 9,
                  fontWeight: 600,
                  backgroundColor: hours
                    ? "var(--dashboard-info-light)"
                    : "var(--dashboard-track-segmented)",
                  color: hours
                    ? "var(--dashboard-info)"
                    : "var(--dashboard-border-divider)",
                }}
              >
                {day[0]}
              </Box>
            );
          })}
        </Box>
        <Typography
          sx={{
            mt: 0.25,
            fontSize: 11,
            color: "var(--dashboard-text-secondary)",
          }}
        >
          {availableDays}d / week
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        border: "1px solid var(--dashboard-border-divider)",
        borderRadius: "var(--dashboard-radius-control)",
      }}
    >
      {CREW_DAYS.map((day) => {
        const hours = availability[day];

        return (
          <Box
            key={day}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
              minHeight: 56,
              px: 1.5,
              py: 0.5,
              "&:not(:last-of-type)": {
                borderBottom: "1px solid var(--dashboard-border-row)",
              },
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={!!hours}
                  disabled={!onChange}
                  onChange={() => handleToggle(day)}
                />
              }
              label={day}
              sx={{
                m: 0,
                minWidth: 80,
                "& .MuiFormControlLabel-label": {
                  fontSize: 14,
                  fontWeight: 600,
                },
              }}
            />
            {hours ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Select
                  size="small"
                  value={hours.start}
                  onChange={handleStartChange(day, hours)}
                  disabled={!onChange}
                  MenuProps={timeMenuProps}
                  inputProps={{ "aria-label": `${day} start time` }}
                  sx={timeSelectSx}
                >
                  {CREW_TIME_SLOTS.slice(0, -1).map((slot) => (
                    <MenuItem key={slot.value} value={slot.value}>
                      {slot.key}
                    </MenuItem>
                  ))}
                </Select>
                <Typography className="dashboard-helper-text">to</Typography>
                <Select
                  size="small"
                  value={hours.end}
                  onChange={handleEndChange(day, hours)}
                  disabled={!onChange}
                  MenuProps={timeMenuProps}
                  inputProps={{ "aria-label": `${day} end time` }}
                  sx={timeSelectSx}
                >
                  {CREW_TIME_SLOTS.filter(
                    (slot) => String(slot.value) > hours.start,
                  ).map((slot) => (
                    <MenuItem key={slot.value} value={slot.value}>
                      {slot.key}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            ) : (
              <Typography className="dashboard-helper-text">
                Unavailable
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
